/**
 * Store links for the SwiftOps mobile app.
 *
 * Used by `AppDownloadModal` (the one-time prompt after an invite is accepted)
 * and by the Account Settings card that keeps the badges reachable afterwards.
 * A person who has just set their password from an invite email has no way of
 * knowing the app exists, and searching a store is not a reliable path to it —
 * the listing has been published under a different name from the product's.
 *
 * Platform detection only decides which link is emphasised — BOTH links always
 * render, so a wrong or unknown guess never hides a store from anyone.
 */

// No `/us/` storefront segment on purpose: Apple resolves the bare id form to
// the VIEWER's own storefront, and this page ships in Spanish too.
export const APP_STORE_URL = 'https://apps.apple.com/app/id6767509084';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=io.swiftpass.swiftops.admin';

export type MobilePlatform = 'ios' | 'android' | 'other';

/**
 * Which mobile store to emphasise, read from the user agent only — pure, so it
 * is unit-testable and can be called from anywhere.
 *
 * Modern iPadOS deliberately reports a Macintosh user agent, and telling it
 * apart from a real Mac needs `navigator.maxTouchPoints` — DOM state, not the
 * UA string. So an iPad reports 'other' here and simply sees both stores at
 * equal weight, which is the correct outcome for a desktop Mac too.
 */
export function detectMobilePlatform(userAgent?: string): MobilePlatform {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}
